import * as React from 'react'
import { Text, TouchableOpacity, View } from 'react-native'
import styled from '@theme/styled-components'
import theme from '@theme/Theme'

import Icon from '@components/icon/Icons'

import useI18n from '@i18n/useI18n'
import useBreakpoint from '@utils/useBreakpoints'

import { EtatDesLieuxTypeEnum } from '@api/enum'

import { isBefore } from 'date-fns'
import { getTenantDates } from '@screens/tenants/tenantsUtils'

interface Props {
  form: EDLForm
  onClick: () => void
  places: LocalisationResponse[]
  manifestation?: ManifestationResponse
  tenant?: Tenant
}

const FormCard = ({ form, onClick, places, manifestation, tenant }: Props) => {
  const i18n = useI18n()
  const [, currentBreakpoint] = useBreakpoint()
  const isPhone = currentBreakpoint === 'phone'

  const requests = form.incidentAcceptIds.length
  const dates = !!tenant ? getTenantDates(tenant.tenant) : undefined

  const dateOpen =
    form.type === EtatDesLieuxTypeEnum.ENTREE
      ? !!manifestation
        ? new Date(manifestation.dateOuverturePublic)
        : !!dates
        ? dates.start
        : undefined
      : undefined

  const dateClose =
    form.type === EtatDesLieuxTypeEnum.SORTIE
      ? !!manifestation
        ? new Date(manifestation.dateFermeturePublic)
        : !!dates
        ? dates.end
        : undefined
      : undefined

  const isPast = !!dateOpen && isBefore(dateOpen, new Date())

  const nameEvent = !!manifestation ? manifestation.libelle : !!tenant ? tenant.tenant.nomLocatairePermanent : ''

  const findPlaces = (ids: string[]) => {
    return ids
      .map((e) => {
        const findPlace = places.find((p) => p.id === e)

        if (!!findPlace) {
          return findPlace.libelle
        }

        return undefined
      })
      .filter(Boolean)
      .join(', ')
  }

  const renderRemainSpaces = () => {
    const placesName = findPlaces(form.espaceIds)

    if (placesName.length > 0) {
      return (
        <SubInfos>
          <EventText numberOfLines={1}>{placesName}</EventText>
        </SubInfos>
      )
    }
  }

  const renderRequestIndicator = (numberToDisplay: number) => (
    <>
      <RequestIndicator>
        <RequestIndicatorTotal>{numberToDisplay}</RequestIndicatorTotal>
      </RequestIndicator>
      {!isPhone && <RequestLabel>{i18n.t('screens.edlList.requests')}</RequestLabel>}
    </>
  )

  return (
    <Container key={form.id} activeOpacity={0.9} onPress={onClick}>
      <InfoContainer>
        <Name numberOfLines={2} ellipsizeMode="tail">
          {nameEvent}
        </Name>
        {renderRemainSpaces()}
        <SubInfos>
          <EventText primary>{i18n.t(`screens.edlList.${form.type}`)}</EventText>
          <EventText>
            {!!dateOpen && i18n.t(`screens.edlList.${isPast ? 'openPast' : 'openFuture'}`, { date: dateOpen })}
            {!!dateClose && i18n.t('screens.edlList.closeDate', { date: dateClose })}
          </EventText>
        </SubInfos>
        {!isPhone && <RequestContainer>{renderRequestIndicator(requests)}</RequestContainer>}
      </InfoContainer>
      {isPhone && (
        <CountContainer>
          {renderRequestIndicator(requests)}
          <View>
            <Icon name="arrow" size={20} color={theme.colors.primary} />
          </View>
        </CountContainer>
      )}
    </Container>
  )
}

export default FormCard

const Container = styled(TouchableOpacity)`
  margin: 4px;
  border-radius: 8px;
  background-color: ${(props) => props.theme.colors.background};
  ${(props) => (props.theme.breakpoint !== 'phone' ? 'width: 360px;' : 'flex-direction: row; align-items: center;')};
  ${(props) => props.theme.constants.shadow};
  padding: ${(props) => (props.theme.breakpoint === 'phone' ? '16px' : '24px')};
  justify-content: space-between;
`
const InfoContainer = styled(View)`
  ${(props) => (props.theme.breakpoint === 'phone' ? 'flex: 1;' : 'flex-grow: 1;')};
`

const Name = styled(Text)<{ isList?: boolean }>`
  ${(props) => (props.theme.breakpoint === 'phone' ? props.theme.fonts.Body : props.theme.fonts.Headline)};
  ${(props) => props.theme.breakpoint !== 'phone' && 'flex-grow: 1;'};
`

const EventText = styled(Text)<{ primary?: boolean; row?: boolean; padding?: boolean }>`
  ${(props) => !!props.padding && 'padding-right: 32px;'}
  ${(props) => !props.padding && !props.row && 'padding-right: 8px;'}
  ${(props) => props.theme.fonts.Footnote};
  ${(props) => !props.primary && 'flex: 1;'}
  color: ${(props) => (props.primary ? props.theme.colors.primary : props.theme.colors.secondaryText)};
`

const RequestIndicator = styled(View)<{ margin?: boolean }>`
  height: ${(props) => (props.theme.breakpoint === 'phone' ? 24 : 32)}px;
  width: ${(props) => (props.theme.breakpoint === 'phone' ? 24 : 32)}px;
  border-radius: 36px;
  background-color: ${(props) => props.theme.colors.secondaryActive};
  align-items: center;
  justify-content: center;
  ${(props) => (props.theme.breakpoint !== 'phone' || props.margin) && `margin-right: 16px;`}
`

const RequestIndicatorTotal = styled(Text)`
  ${(props) => (props.theme.breakpoint === 'phone' ? props.theme.fonts.Caption1 : props.theme.fonts.Subheadline)};
  text-align: center;
  color: ${(props) => props.theme.colors.primaryText};
  align-items: center;
`

const RequestLabel = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  color: ${(props) => props.theme.colors.secondaryText};
  font-size: ${(props) => (props.theme.breakpoint === 'phone' ? 11 : 16)}px;
`
const CountContainer = styled(View)`
  flex-direction: row;
`

const SubInfos = styled(View)`
  flex-direction: row;
  padding-top: ${(props) => (props.theme.breakpoint === 'phone' ? '8' : '12')}px;
  align-items: center;
`

const RequestContainer = styled(View)`
  margin-top: 20px;
  background-color: ${(props) => props.theme.colors.cardSecondary};
  padding: 22px;
  align-items: center;
  flex-direction: row;
`
